import {
  Flex,
  Heading,
  Skeleton,
  Box,
  Tabs,
  TabList,
  Tab,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  Maybe,
  useGetCreditFacilityQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  useGetBorrowingBaseReportsQuery,
  useGetLatestBorrowingBaseReportQuery,
} from '../requests/borrowingBase.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { CreditFacilitySelect } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

import '../../styles/common.scss';

export enum TAB_OPTION_KEYS {
  fundingOverview = 'fundingOverview',
  borrowingBase = 'borrowingBase',
  financialCovenants = 'financialCovenants',
  portfolioCovenants = 'portfolioCovenants',
  performance = 'performance',
  ltv = 'ltv',
  outstandingBalance = 'outstandingBalance',
  expensesFees = 'expensesFees',
  eligibility = 'eligibility',
  concentration = 'concentration',
}

type TabType = { key: TAB_OPTION_KEYS; label: string; route: string };
const TAB_OPTIONS: Record<TAB_OPTION_KEYS, TabType> = {
  fundingOverview: {
    key: TAB_OPTION_KEYS.fundingOverview,
    label: 'Funding Overview',
    route: '/insights/funding-overview',
  },
  borrowingBase: {
    key: TAB_OPTION_KEYS.borrowingBase,
    label: 'Borrowing base',
    route: '/insights/borrowing-base',
  },
  financialCovenants: {
    key: TAB_OPTION_KEYS.financialCovenants,
    label: 'Financial covenants',
    route: '/insights/financial-covenants',
  },
  portfolioCovenants: {
    key: TAB_OPTION_KEYS.portfolioCovenants,
    label: 'Portfolio covenants',
    route: '/insights/portfolio-covenants',
  },
  performance: {
    key: TAB_OPTION_KEYS.performance,
    label: 'Performance',
    route: '/insights/performance',
  },
  ltv: {
    key: TAB_OPTION_KEYS.ltv,
    label: 'LTV',
    route: '/insights/ltv',
  },
  outstandingBalance: {
    key: TAB_OPTION_KEYS.outstandingBalance,
    label: 'Outstanding balance',
    route: '/insights/outstanding-balance',
  },
  expensesFees: {
    key: TAB_OPTION_KEYS.expensesFees,
    label: 'Expenses & Fees',
    route: '/insights/expenses-fees',
  },
  eligibility: {
    key: TAB_OPTION_KEYS.eligibility,
    label: 'Eligibility',
    route: '/insights/eligibility',
  },
  concentration: {
    key: TAB_OPTION_KEYS.concentration,
    label: 'Concentration limits',
    route: '/insights/concentration',
  },
};

export const InsightsTemplate = ({
  children,
  selectedTab,
  organizationId,
}: {
  children: React.ReactChild | React.ReactChild[];
  selectedTab: TAB_OPTION_KEYS;
  organizationId?: Maybe<string>;
}) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const { creditFacilityId } = useCreditFacilityContext();
  const {
    data: creditFacilityData,
    loading: creditFacilityLoading,
  } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });
  const { pathname: currentPath } = useLocation();

  const {
    data: borrowingBaseReportsData,
    loading: borrowingBaseReportsLoading,
  } = useGetBorrowingBaseReportsQuery({
    variables: {
      creditFacilityId,
    },
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, navigate);
    },
  });

  const {
    data: borrowingBaseReportData,
    loading: borrowingBaseReportLoading,
  } = useGetLatestBorrowingBaseReportQuery({
    variables: {
      creditFacilityId,
    },
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, navigate);
    },
  });

  const tabs = useMemo<TabType[]>(() => {
    return [
      TAB_OPTIONS.fundingOverview,
      TAB_OPTIONS.borrowingBase,
      TAB_OPTIONS.eligibility,
      TAB_OPTIONS.concentration,
      TAB_OPTIONS.financialCovenants,
      ...(flags?.displayPortfolioCovenants
        ? [TAB_OPTIONS.portfolioCovenants]
        : []),
      TAB_OPTIONS.performance,
      TAB_OPTIONS.ltv,
      TAB_OPTIONS.expensesFees,
    ];
  }, [flags]);

  if (!tabs.length || creditFacilityLoading || !creditFacilityData) {
    return <Skeleton data-testid="loading-insights-view" height="600px" />;
  }

  if (
    borrowingBaseReportLoading ||
    !borrowingBaseReportData ||
    borrowingBaseReportsLoading ||
    !borrowingBaseReportsData
  ) {
    return <Skeleton data-testid="loading-insights-view" height="600px" />;
  }

  // If currentPath doesn't exist as available tab, redirect to first available tab
  if (!tabs.some(({ route }) => route === currentPath)) {
    navigate(tabs[0].route);
    return null;
  }

  return (
    <>
      <Box background="white" id="insights-template-header">
        <Flex justify="space-between">
          <Heading as="h2">Insights</Heading>
          <Box>
            <CreditFacilitySelect organizationId={organizationId} />
          </Box>
        </Flex>
      </Box>

      <Tabs
        variant="line"
        index={tabs.findIndex(({ key }) => key === selectedTab)}
        colorScheme="blue"
        background="white"
      >
        <TabList>
          {tabs.map(({ route, label, key }) => (
            <Tab
              key={key}
              onClick={() => {
                navigate(route);
              }}
            >
              {label}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Box mt="32px" id="insights-template-wrapper">
        {children}
      </Box>
    </>
  );
};

export const InsightsHighlightBox = ({
  children,
}: {
  children: React.ReactChild | React.ReactChild[];
}) => {
  return (
    <Box
      background="white"
      borderRadius="8px"
      padding="32px"
      mb="32px"
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
    >
      {children}
    </Box>
  );
};
